// How the CLIENT paid for a booking request — the one resolver every staff
// surface uses.
//
// Why this exists: the admin review modal used to show nothing at all, so a
// card-paid booking was indistinguishable from a wallet-funded one and staff
// read "wallet" by default. Saying "Paid from wallet" about a card payment is
// a money lie in the direction that costs us a chargeback, so the resolver is
// deliberately conservative: it only claims a rail it has positive evidence
// for, and otherwise says "Not recorded" rather than guessing wallet.
//
// Evidence, most authoritative first:
//   1. request.payment_method — the explicit field written at submit time by
//      the backend contract ('card' | 'wallet' | 'wallet_card' | 'auto_charge'
//      | 'invoice_later').
//   2. payload.payment_mode — the pre-existing auto-charge marker
//      (clientChangeRequests.js sets payload.payment_mode='auto_charge').
//   3. A linked gateway checkout (order_ref / checkout_status / gateway_state)
//      → card, and its live state tells staff whether the money has landed.
//   4. A linked BANK-TRANSFER deposit → the deposit rail.
// No evidence at all → UNKNOWN. Never defaults to wallet.

export const PAYMENT_RAILS = {
    card: {
        key: 'card',
        label: 'Paid by card',
        detail: 'Card payment through the payment gateway.',
        className: 'pcr-payrail--card',
        icon: '💳',
    },
    wallet: {
        key: 'wallet',
        label: 'Paid from wallet',
        detail: 'Charged against the client’s wallet balance.',
        className: 'pcr-payrail--wallet',
        icon: '👛',
    },
    wallet_card: {
        key: 'wallet_card',
        label: 'Paid by wallet + card',
        detail: 'Part covered by the wallet balance, the remainder captured on a card.',
        className: 'pcr-payrail--card',
        icon: '💳',
    },
    auto_charge: {
        key: 'auto_charge',
        label: 'Auto-charge on completion',
        detail: 'Nothing has been taken yet — the saved card is charged when the booking completes.',
        className: 'pcr-payrail--auto',
        icon: '⏱️',
    },
    bank_transfer: {
        key: 'bank_transfer',
        label: 'Paid by bank transfer',
        detail: 'Backed by a bank-transfer deposit that must be approved in Wallet Management.',
        className: 'pcr-payrail--bank',
        icon: '🏦',
    },
    invoice_later: {
        key: 'invoice_later',
        label: 'To be invoiced',
        detail: 'Not paid up front — this booking goes on the client’s invoice.',
        className: 'pcr-payrail--invoice',
        icon: '🧾',
    },
    unknown: {
        key: 'unknown',
        label: 'Payment method not recorded',
        detail: 'No payment rail was recorded for this request — check the client’s wallet before approving.',
        className: 'pcr-payrail--unknown',
        icon: '❓',
    },
};

const normalise = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

// ---------------------------------------------------------------------------
// Backend field contract (routes/clientChangeRequests.js, GET / and GET /:id).
//
// The admin request row does NOT carry `payment_method` / `checkout_status` /
// `gateway_state` / `order_ref`. It carries the B8 aliases below. Reading the
// short names silently yielded `undefined` everywhere, which meant the rail
// fell back to a guess and — far worse — resolvePaymentStatus could never
// raise the "do not approve yet" warnings. Read the real names first, keep the
// short names as a fallback so any other caller shape still works.
// ---------------------------------------------------------------------------

const firstDefined = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '');

export const readCheckoutStatus = (request) =>
    normalise(firstDefined(request.client_payment_checkout_status, request.checkout_status));

export const readGatewayState = (request) =>
    normalise(firstDefined(request.client_payment_gateway_state, request.gateway_state));

export const readOrderRef = (request) =>
    firstDefined(request.client_payment_order_ref, request.order_ref);

// Backend-computed: a gateway order exists but the checkout is still 'created'
// (money staged, result not reconciled). Positive evidence of an UNFUNDED card.
export const readUnconfirmed = (request) =>
    request.client_payment_unconfirmed === true || request.client_payment_unconfirmed === 'true';

// Gateway states that mean money is authorised/held/captured at the gateway.
const GATEWAY_MONEY_STATES = ['authorised', 'authorized', 'captured', 'purchased', 'post_auth_refer', 'review'];

export function resolveRequestPaymentRail(request) {
    if (!request) return PAYMENT_RAILS.unknown;
    const payload = request.payload || {};

    // 1. The backend's authoritative signal (B8). NOTE: the SQL CASE ends in
    //    `ELSE 'wallet'`, so 'wallet' there is a DEFAULT, not evidence — it is
    //    emitted for any request with no gateway checkout and no payment_mode.
    //    Every other value is derived from real evidence, so trust those
    //    directly and let 'wallet' fall through to the corroboration below
    //    rather than reintroducing the "always says wallet" money lie.
    const backendRail = normalise(request.client_payment_method);
    if (backendRail && backendRail !== 'wallet' && PAYMENT_RAILS[backendRail]) {
        return PAYMENT_RAILS[backendRail];
    }

    const explicit = normalise(request.payment_method || payload.payment_method);
    if (explicit && PAYMENT_RAILS[explicit]) return PAYMENT_RAILS[explicit];

    const mode = normalise(request.payment_mode || payload.payment_mode);
    if (mode && PAYMENT_RAILS[mode]) return PAYMENT_RAILS[mode];

    // A gateway checkout exists for this request → it was a card payment,
    // whatever else is (or isn't) recorded.
    if (readOrderRef(request) || readCheckoutStatus(request) || readGatewayState(request) || readUnconfirmed(request)) {
        return PAYMENT_RAILS.card;
    }

    if (request.linked_deposit_id) {
        const dm = normalise(request.linked_deposit_method || request.linked_deposit_payment_method);
        if (dm === 'online_payment' || dm === 'card') return PAYMENT_RAILS.card;
        return PAYMENT_RAILS.bank_transfer;
    }

    return PAYMENT_RAILS.unknown;
}

// Whether the money has actually landed, for the rails where that is knowable.
// Returns null when there's nothing meaningful to say.
export function resolvePaymentStatus(request) {
    if (!request) return null;
    const state = readGatewayState(request);
    const checkout = readCheckoutStatus(request);
    const unconfirmed = readUnconfirmed(request);

    if ((checkout === 'created' || unconfirmed) && GATEWAY_MONEY_STATES.includes(state)) {
        return {
            tone: 'warn',
            label: 'Payment in review — do not approve yet',
            detail: 'The bank is holding this card payment. It is not confirmed, so the booking is not funded yet.',
        };
    }
    if (checkout === 'created' || unconfirmed) {
        return {
            tone: 'warn',
            label: 'Card payment not confirmed',
            detail: 'The client opened a payment page but we have not reconciled a result. Do not approve until it settles.',
        };
    }
    if (checkout === 'paid' || checkout === 'settled' || GATEWAY_MONEY_STATES.includes(state)) {
        return { tone: 'ok', label: 'Payment received', detail: '' };
    }
    if (request.linked_deposit_status === 'pending') {
        return {
            tone: 'warn',
            label: 'Deposit pending approval',
            detail: 'Approve the deposit in Wallet Management first — the wallet is not credited yet.',
        };
    }
    return null;
}
